"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SLIDER_BAR_HEIGHT = void 0;
exports.clamp = clamp;
exports.getNearestStepValue = getNearestStepValue;
exports.getPositionFromValue = getPositionFromValue;
exports.useEventCallback = useEventCallback;
exports.useFirstMountState = useFirstMountState;
var _react = require("react");
const SLIDER_BAR_HEIGHT = exports.SLIDER_BAR_HEIGHT = 4;

// Used for SSR
const useEnhancedEffect = typeof window !== 'undefined' ? _react.useLayoutEffect : _react.useEffect;

/**
 * Rounds value to nearest step.
 *
 * @param {number} value value to be rounded
 * @param {number} min minimum slider value
 * @param {number} max maximum slider value
 * @param {number} step the amount the handler skips over
 * @returns {number} Value of step nearest to given value
 */
function getNearestStepValue(value, min, max, step) {
  // This is necessary only when the min is negative
  if (value >= max) {
    return max;
  }
  const nearestStepValue = min + step * getStepIndex(value, min, max, step);
  if (nearestStepValue > max && nearestStepValue < min) {
    return nearestStepValue;
  }
  return clamp(nearestStepValue, min, max);
}

/**
 * Helper function that returns a step index.
 *
 * @param {number} value value to find index of
 * @param {number} min maximum slider value
 * @param {number} max minimum slider value
 * @param {number} step the amount the handler skips over
 * @returns {number} Index of step
 */
function getStepIndex(value, min, max, step) {
  // Math.round is necessary for smooth transitions, but when
  // decrementing from max, the value should only ever decrease by one step.
  if (value + step === max) {
    return Math.ceil((value - min) / step);
  }
  return Math.round((value - min) / step);
}

/**
   * Clamps value to specified range.
   *
   * @param {number} value Value to be clamped
   * @param {number} min Lower end of range
   * @param {number} max Upper end of range
   * @returns {number} The clamped value
   */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Returns position associated with specified value.
 *
 * @param {number} val Value to find position of
 * @param {number} min min slider value
 * @param {number} max max slider value
 * @param {number} clientWidth current slider width
 * @returns {number} Position in pixels of specified value
 */
function getPositionFromValue(val, min, max, clientWidth) {
  const widthPercent = (val - min) / (max - min);
  return SLIDER_BAR_HEIGHT / 2 + clientWidth * widthPercent;
}

/*
  https://github.com/facebook/react/issues/14099#issuecomment-440013892
  This allows us to keep a shallow reference of the callback function as per the issue above
  useEffect/useLayoutEffect is very fragile and allows us to memoize without
  needing to pass additional args
*/
/**
 *  Returns a memoized callback function
 *
 * @param {function} fn callback function
 * @returns {function} memoized callback funtion
 */
function useEventCallback(fn) {
  const ref = (0, _react.useRef)(fn);
  useEnhancedEffect(() => {
    ref.current = fn;
  });
  return (0, _react.useCallback)(function () {
    return ref.current(...arguments);
  }, []);
}

/**
 * Returns true if first mount
 *
 * @returns {boolean} true if first mount, false otherwise
 */
function useFirstMountState() {
  const isFirst = (0, _react.useRef)(true);
  if (isFirst.current) {
    isFirst.current = false;
    return true;
  }
  return isFirst.current;
}